import axios from "axios";
import { axiosInstanceWithToken } from "@/utils/axios";
import { HOST_API_KEY } from "../../config";

const SEO_REQUEST_TIMEOUT_MS = 800;

const getClientApiEndpoint = (path) =>
  typeof window === "undefined" ? `${HOST_API_KEY}${path}` : `/api${path}`;

export const createPlace = async (data) => {
  const request = await axiosInstanceWithToken.post(`${HOST_API_KEY}/places`, data);
  return request?.data;
};

export const getAllPlaces = async () => {
  const request = await axios.get(`${HOST_API_KEY}/places`);
  return request?.data;
};

export const getAllPlacesWithReviews = async (params) => {
  const request = await axios.get(`${HOST_API_KEY}/places/with-reviews`, {
    params,
  });
  return request?.data;
};

export const getPlaceSearchSuggestions = async (params) => {
  const endpoint =
    typeof window === "undefined"
      ? `${HOST_API_KEY}/places/search-suggestions`
      : "/api/places/search-suggestions";

  const request = await axios.get(endpoint, {
    params,
    timeout: 8000,
  });
  return request?.data;
};

export const getAllPlacesWithReviewsByPlaceId = async (placeId, userId) => {
  const request = await axios.get(
    getClientApiEndpoint(`/places/${placeId}/with-reviews`),
    {
      params: {
        userId,
      },
    }
  );
  return request?.data;
};

export const getAllPlaceMenusByPlaceId = async (placeId) => {
  const request = await axios.get(
    getClientApiEndpoint(`/places/${placeId}/menus`)
  );
  return request?.data;
};

export const createPlaceSearchHistory = async (data) => {
  const request = await axiosInstanceWithToken.post(
    `${HOST_API_KEY}/places/search-history/create`,
    data
  );
  return request?.data;
};

export const getPlaceSearchHistory = async (userId) => {
  const request = await axiosInstanceWithToken.get(
    `${HOST_API_KEY}/places/search-history/${userId}`
  );
  return request?.data;
};

export const deletePlaceSearchHistory = async (params) => {
  const request = await axiosInstanceWithToken.delete(
    `${HOST_API_KEY}/places/search-history/delete`,
    {
      params: {
        searchIds: params,
      },
    }
  );
  return request?.data;
};

export const getAllPlaceMediaByPlaceId = async (placeId) => {
  const request = await axios.get(`${HOST_API_KEY}/places/${placeId}/media`);
  return request?.data;
};

export const getPlaceReviewsSummaryByPlaceId = async (placeId) => {
  const request = await axios.get(
    `${HOST_API_KEY}/places/${placeId}/reviews-summary`
  );
  return request?.data;
};

export const getMapPinsWithinBounds = async (params) => {
  const request = await axios.get(`${HOST_API_KEY}/places/map-pins`, {
    params,
  });
  return request?.data;
};

export const fetchLocationsAroundPosition = async (params) => {
  const request = await axios.get(`${HOST_API_KEY}/places/around-position`, {
    params,
  });
  return request?.data;
};

export const getPlaceAverageRatingByPlaceId = async (params) => {
  const request = await axios.get(`${HOST_API_KEY}/places/average-rating`, {
    params,
  });
  return request?.data;
};

export const createPlaceViewHistory = async (data) => {
  const request = await axiosInstanceWithToken.post(
    `${HOST_API_KEY}/places/view-history/create`,
    data
  );
  return request?.data;
};

export const getAllPlaceMediaForWindowByPlaceId = async (placeId) => {
  const request = await axios.get(
    getClientApiEndpoint(`/places/${placeId}/media-for-window`)
  );
  return request?.data;
};

export const getPlaceTextReviewByPlaceId = async (placeId, params) => {
  const request = await axios.get(
    getClientApiEndpoint(`/places/${placeId}/text-reviews`),
    {
      params,
    }
  );
  return request?.data;
};

export const getPlaceSearchForUpdate = async (params) => {
  const request = await axios.get(`${HOST_API_KEY}/places/search-for-update`, {
    params,
  });
  return request?.data;
};

export const getAllPlaceDetailsByPlaceId = async (placeId, userId, langDivCd = 'KOR') => {
  const request = await axios.get(
    getClientApiEndpoint(`/places/${placeId}/all-details`),
    { params: { userId, langDivCd } }
  );
  return request?.data;
};

export const getPlaceByPublicId = async (publicId) => {
  const request = await axios.get(`${HOST_API_KEY}/places/public/${publicId}`);
  return request?.data;
};

export const updatePlaceReviewByReviewId = async (id, data) => {
  const request = await axiosInstanceWithToken.put(
    `${HOST_API_KEY}/places/reviews/edit/${id}`,
    data
  );
  return request?.data;
};

export const updatePlaceByPlaceId = async (id, data) => {
  const request = await axiosInstanceWithToken.put(
    `${HOST_API_KEY}/places/details/edit/${id}`,
    data
  );
  return request?.data;
};

export const createPlaceMediaByAdmin = async (data) => {
  const request = await axiosInstanceWithToken.post(
    `${HOST_API_KEY}/places/media/create`,
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return request?.data;
};

export const updatePlaceMenuByAdmin = async (id, data) => {
  const request = await axiosInstanceWithToken.put(
    `${HOST_API_KEY}/places/menus/edit/${id}`,
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return request?.data;
};

export const addFavoritePlace = async (data) => {
  const request = await axiosInstanceWithToken.post(
    `${HOST_API_KEY}/places/favorites/create`,
    data
  );
  return request?.data;
};

export const removeFavoritePlace = async (data) => {
  const request = await axiosInstanceWithToken.delete(
    `${HOST_API_KEY}/places/favorites/delete`,
    {
      params: data,
    }
  );
  return request?.data;
};

export const createPlaceSuggestion = async (data) => {
  const request = await axiosInstanceWithToken.post(
    `${HOST_API_KEY}/places/suggestions/create`,
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return request?.data;
};

export const getPlaceLocationByPlaceId = async (placeId) => {
  const request = await axios.get(`${HOST_API_KEY}/places/location/${placeId}`);
  return request?.data;
};

export const createPlaceReview = async (data) => {
  const request = await axiosInstanceWithToken.post(
    `${HOST_API_KEY}/places/reviews/create`,
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return request?.data;
};

export const getPlacePostsByPlaceId = async (placeId, params) => {
  const request = await axios.get(
    getClientApiEndpoint(`/place/${placeId}/posts`),
    {
      params,
    }
  );
  return request?.data;
};

export const likePlaceReview = async (data) => {
  const request = await axiosInstanceWithToken.post(`${HOST_API_KEY}/place/like-review`, data);
  return request.data;
};

export const unlikePlaceReview = async (data) => {
  const request = await axiosInstanceWithToken.delete(`${HOST_API_KEY}/place/unlike-review`, {
    params: data,
  });
  return request.data;
};

export const unlikePlacePost = async (data) => {
  const request = await axiosInstanceWithToken.delete(`${HOST_API_KEY}/place/unlike-post`, {
    params: data,
  });
  return request.data;
};

export const likePlacePost = async (data) => {
  const request = await axiosInstanceWithToken.post(`${HOST_API_KEY}/place/like-post`, data);
  return request.data;
};

export const fetchPlaceDetailsForEdit = async (placeId) => {
  const request = await axiosInstanceWithToken.get(
    `${HOST_API_KEY}/place/${placeId}/details-for-edit`
  );
  return request?.data;
};

export const updateMenuByMenuId = async (id, data) => {
  const request = await axiosInstanceWithToken.put(
    `${HOST_API_KEY}/places/menus-edit/${id}`,
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return request?.data;
};

export const createPlaceFavList = async (data) => {
  const request = await axiosInstanceWithToken.post(
    `${HOST_API_KEY}/place/fav-lists/create`,
    data
  );
  return request?.data;
};

export const getPlaceFavListsByUserId = async (params) => {
  const request = await axiosInstanceWithToken.get(`${HOST_API_KEY}/place/fav-lists/by-user`, {
    params,
  });
  return request?.data;
};

export const getPlaceFavListByListId = async (params) => {
  const request = await axios.get(`${HOST_API_KEY}/place/fav-lists/by-listId`, {
    params,
  });
  return request?.data;
};

export const updatePlaceListByListId = async (id, data) => {
  const request = await axiosInstanceWithToken.put(
    `${HOST_API_KEY}/place/fav-lists/update-by-listId/${id}`,
    data
  );
  return request?.data;
};

export const deletePlaceListByListId = async (id) => {
  const request = await axiosInstanceWithToken.delete(
    `${HOST_API_KEY}/place/fav-lists/delete-by-listId/${id}`
  );
  return request?.data;
};

export const moveFavoritePlacesFromLists = async (data) => {
  const request = await axiosInstanceWithToken.put(
    `${HOST_API_KEY}/place/fav-lists/move-favorites`,
    data
  );
  return request?.data;
};

export const deletePlacesFromLists = async (data) => {
  const request = await axiosInstanceWithToken.delete(
    `${HOST_API_KEY}/place/fav-lists/delete-favorites`,
    { params: data }
  );
  return request?.data;
};

export const changeFavListVisibility = async (id) => {
  const request = await axiosInstanceWithToken.put(
    `${HOST_API_KEY}/place/fav-lists/visibility/${id}`
  );
  return request?.data;
};

export const createAddPlaceSuggestion = async (data) => {
  const request = await axiosInstanceWithToken.post(
    `${HOST_API_KEY}/places/add-place-suggestions/create`,
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return request?.data;
};

export const getPlaceSeoDetailsByPlaceId = async (placeId) => {
  const request = await axios.get(
    `${HOST_API_KEY}/places/seo/_single/${placeId}`,
    { timeout: SEO_REQUEST_TIMEOUT_MS }
  );
  return request?.data;
};

export const getSeoDetailsForPlacesPage = async (params) => {
  const request = await axios.get(`${HOST_API_KEY}/places/seo/for_page`, {
    params,
    timeout: SEO_REQUEST_TIMEOUT_MS,
  });
  return request?.data;
};

export const getAdditionalSeoMetaTagsForPlacesPage = async (params) => {
  const request = await axios.get(
    `${HOST_API_KEY}/places/seo/additional_meta_tags`,
    { params, timeout: SEO_REQUEST_TIMEOUT_MS }
  );
  return request?.data;
};

export const addOrUpdatePlacePhotos = async (data) => {
  const request = await axiosInstanceWithToken.post(
    `${HOST_API_KEY}/places/modify-place-media`,
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return request?.data;
};

export const deletePlacePhoto = async (id) => {
  const request = await axiosInstanceWithToken.delete(
    `${HOST_API_KEY}/places/delete-media/${id}`
  );
  return request?.data;
};

export const deletePlaceMenuMedia = async (id) => {
  const request = await axiosInstanceWithToken.delete(
    `${HOST_API_KEY}/places/delete-menu-media/${id}`
  );
  return request?.data;
};

/**
 * 내 검색 기록을 서버에서 통째로 지운다.
 * ⚠️ 브라우저 것(localStorage)만 지우면 화면을 다시 열 때 되살아난다 —
 *    두 곳에 저장되므로 **둘 다** 지워야 한다.
 */
export const clearMySearchHistory = async () => {
  const request = await axiosInstanceWithToken.delete(
    `${HOST_API_KEY}/search-history/clear`
  );
  return request?.data;
};
